import React, { useEffect, useState } from 'react';
import { Button, Card, StatusBadge } from '../common';
import { Heading, Text, Typography } from '../common/Typography';
import ClubBanner from './ClubBanner';
import {
  requestClubReactivation,
  withdrawClubReactivation,
} from '../../services/clubMembershipService';
import { fetchClubBalancedRecord } from '../../services/clubDashboardService';
import { supabase } from '../../utils/supabase';

const InactiveClubMembershipView = ({
  club,
  clubId,
  currentUser,
  membership,
  onBack,
  backLabel = 'Back to My Clubs',
  onMembershipChange,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [record, setRecord] = useState(null);
  const [ratings, setRatings] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const requestedAt = membership?.reactivation_requested_at;

  useEffect(() => {
    if (!currentUser?.id || !clubId) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const [{ data: seasons }, balanced] = await Promise.all([
          supabase
            .from('seasons')
            .select('id, name, season_status')
            .eq('club_id', clubId)
            .eq('season_status', 'current')
            .order('created_at', { ascending: false })
            .limit(1),
          fetchClubBalancedRecord(currentUser.id, clubId),
        ]);
        if (cancelled) return;
        setRecord(balanced);

        const seasonId = seasons?.[0]?.id;
        if (seasonId) {
          const { data: pcr } = await supabase
            .from('player_club_elo_ratings')
            .select('mixed_elo_rating, levels_elo_rating')
            .eq('club_id', clubId)
            .eq('player_id', currentUser.id)
            .eq('season_id', seasonId)
            .maybeSingle();
          if (!cancelled) setRatings(pcr || null);
        }

        const { data: sessions } = await supabase
          .from('session_players')
          .select(`
            session_id,
            sessions:session_id (
              id,
              name,
              session_date,
              session_status
            )
          `)
          .eq('player_id', currentUser.id)
          .eq('club_id', clubId)
          .order('joined_at', { ascending: false })
          .limit(20);

        if (cancelled) return;
        const played = (sessions || [])
          .map((row) => row.sessions)
          .filter((session) => session?.id && ['completed', 'archived'].includes(session.session_status))
          .slice(0, 5);
        setRecentSessions(played);
      } catch (err) {
        console.error('Failed to load inactive membership history:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clubId, currentUser?.id]);

  const handleRequest = async () => {
    setBusy(true);
    setError(null);
    try {
      await requestClubReactivation(clubId);
      onMembershipChange?.({ reactivation_requested_at: new Date().toISOString() });
    } catch (err) {
      setError(err.message || 'Failed to request reactivation');
    } finally {
      setBusy(false);
    }
  };

  const handleWithdraw = async () => {
    setBusy(true);
    setError(null);
    try {
      await withdrawClubReactivation(clubId);
      onMembershipChange?.({ reactivation_requested_at: null });
    } catch (err) {
      setError(err.message || 'Failed to withdraw request');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" onClick={onBack}>
          {backLabel}
        </Button>
      </div>

      <ClubBanner
        bannerImageUrl={club?.banner_image_url || null}
        clubName={club?.name}
        location={club?.location}
        logoImageUrl={club?.logo_image_url || null}
        variant="page"
      />

      <Card className="p-5 sm:p-6">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Heading level={2}>{club?.name || 'Club'}</Heading>
          <StatusBadge variant="inactive">Inactive</StatusBadge>
          {membership?.is_trial ? <StatusBadge variant="warning">On trial</StatusBadge> : null}
        </div>
        <Text className="text-text-secondary">
          You can no longer see club activity or sign up for sessions here. Your past games and rating at this club stay on your record.
        </Text>
        {error ? <p className="mt-3 text-sm text-danger">{error}</p> : null}
        <div className="mt-4">
          {requestedAt ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <StatusBadge variant="pending">Reactivation requested</StatusBadge>
              <Button variant="outline" onClick={handleWithdraw} disabled={busy}>
                {busy ? 'Updating…' : 'Withdraw request'}
              </Button>
            </div>
          ) : (
            <Button variant="primary" onClick={handleRequest} disabled={busy}>
              {busy ? 'Sending…' : 'Request to reactivate'}
            </Button>
          )}
        </div>
      </Card>

      <Card className="p-5 sm:p-6">
        <Heading level={3} className="mb-4">Your record at this club</Heading>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div>
            <Typography variant="caption" className="text-text-muted">Mixed Elo</Typography>
            <p className="text-xl font-bold tabular-nums text-text-primary">
              {ratings?.mixed_elo_rating ?? '—'}
            </p>
          </div>
          <div>
            <Typography variant="caption" className="text-text-muted">Doubles Elo</Typography>
            <p className="text-xl font-bold tabular-nums text-text-primary">
              {ratings?.levels_elo_rating ?? '—'}
            </p>
          </div>
          <div>
            <Typography variant="caption" className="text-text-muted">Games</Typography>
            <p className="text-xl font-bold tabular-nums text-text-primary">
              {record?.gamesPlayed ?? '—'}
            </p>
          </div>
          <div>
            <Typography variant="caption" className="text-text-muted">Win rate</Typography>
            <p className="text-xl font-bold tabular-nums text-text-primary">
              {record?.gamesPlayed ? `${record.winRatePct}%` : '—'}
            </p>
          </div>
        </div>
      </Card>

      <Card className="p-5 sm:p-6">
        <Heading level={3} className="mb-4">Past sessions</Heading>
        {recentSessions.length === 0 ? (
          <Typography variant="muted">No completed sessions on your record yet.</Typography>
        ) : (
          <ul className="space-y-2">
            {recentSessions.map((session) => (
              <li
                key={session.id}
                className="flex items-center justify-between rounded-lg border border-border-medium bg-surface-subtle px-3 py-2"
              >
                <span className="text-sm font-medium text-text-primary">
                  {session.name || 'Session'}
                </span>
                <span className="text-xs text-text-muted">
                  {session.session_date
                    ? new Date(session.session_date).toLocaleDateString('en-GB')
                    : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};

export default InactiveClubMembershipView;
